import { Highlight } from "@tiptap/extension-highlight";
import { Plugin } from "@tiptap/pm/state";

import { SUGGESTION_TYPE_LABELS } from "features/Influencer/Templates/SuggestionPopup/schema";

// Toggled on the editor root so CSS can hide the hover tooltip once the user
// clicks a highlight (the full suggestion popup takes over); re-enabled when the
// pointer leaves the editor.
const BUBBLE_OFF_CLASS = "pm-hl-bubble-off";

const DEFAULT_HIGHLIGHT_COLOR = "#fff4c6";

type SuggestionSeverity = "error" | "tip" | "heads-up";

// Hardcoded mapping of each suggestion type to a severity bucket. Severity drives
// the highlight color: error -> red, tip -> yellow, heads-up -> purple.
const SUGGESTION_TYPE_SEVERITY: Record<string, SuggestionSeverity> = {
  link_accuracy: "error",
  merge_tags: "error",
  call_to_action: "heads-up",
  opening_hook: "heads-up",
  overused_phrases: "tip",
  social_proof: "tip",
  hyperlink: "heads-up",
};

const CustomHighlight = Highlight.extend({
  addAttributes() {
    return {
      color: {
        default: DEFAULT_HIGHLIGHT_COLOR,
        parseHTML: (element) =>
          element.getAttribute("data-color") ||
          element.style.backgroundColor ||
          DEFAULT_HIGHLIGHT_COLOR,
        renderHTML: (attributes) => {
          if (attributes.suggestionType) return {};
          const color = attributes.color || DEFAULT_HIGHLIGHT_COLOR;
          return {
            "data-color": color,
            style: `background-color: ${color}; color: inherit`,
          };
        },
      },
      suggestionType: {
        default: null,
        parseHTML: (element) =>
          element.getAttribute("data-suggestion-type") || null,
        renderHTML: (attributes) => {
          const type = attributes.suggestionType;
          if (!type) return {};
          const severity = SUGGESTION_TYPE_SEVERITY[type] ?? "tip";
          return {
            "data-suggestion-type": type,
            "data-title": SUGGESTION_TYPE_LABELS[type] ?? "Suggestion",
            class: `suggestion-hl suggestion-hl--${severity}`,
          };
        },
      },
    };
  },
  addProseMirrorPlugins() {
    return [
      new Plugin({
        props: {
          handleDOMEvents: {
            mousedown: (view, event) => {
              const target = event.target as HTMLElement | null;
              if (target?.closest?.("mark.suggestion-hl")) {
                view.dom.classList.add(BUBBLE_OFF_CLASS);
              }
              return false;
            },
            mouseleave: (view) => {
              view.dom.classList.remove(BUBBLE_OFF_CLASS);
              return false;
            },
          },
        },
      }),
    ];
  },
});

export default CustomHighlight;
